import React, { createContext, useContext, useState, useEffect, useCallback, ReactNode } from 'react';
import { NostrRelayManager } from '../services/nostr/NostrRelayManager';
import { NostrIdentity } from '../services/nostr/NostrIdentity';
import { IndexedDBStorage } from '../services/storage/IndexedDBStorage';
import { SociiMessage } from '../types/nostr';

interface NostrContextType {
  relayManager: NostrRelayManager | null;
  identity: NostrIdentity | null;
  messages: SociiMessage[];
  currentChannel: string;
  isConnected: boolean;
  isLoading: boolean;
  error: string | null;

  // Actions
  connect: () => Promise<void>;
  sendMessage: (content: string) => Promise<void>;
  switchChannel: (channelId: string) => Promise<void>;
  disconnect: () => void;
}

const NostrContext = createContext<NostrContextType | null>(null);

export const useNostr = () => {
  const context = useContext(NostrContext);
  if (!context) {
    throw new Error('useNostr must be used within a NostrProvider');
  }
  return context;
};

interface NostrProviderProps {
  children: ReactNode;
}

export const NostrProvider: React.FC<NostrProviderProps> = ({ children }) => {
  const [relayManager] = useState(() => new NostrRelayManager());
  const [storage] = useState(() => new IndexedDBStorage());
  const [identity, setIdentity] = useState<NostrIdentity | null>(null);
  const [messages, setMessages] = useState<SociiMessage[]>([]);
  const [currentChannel, setCurrentChannel] = useState<string>('global');
  const [isConnected, setIsConnected] = useState(false);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const initializeSocii = useCallback(async () => {
    try {
      setIsLoading(true);
      setError(null);

      // Initialize storage
      await storage.initialize();

      // Load or create identity
      const savedIdentity = await storage.getIdentity();
      let userIdentity: NostrIdentity;

      if (savedIdentity) {
        userIdentity = await NostrIdentity.fromPrivateKeyHex(savedIdentity.privateKeyHex);
      } else {
        userIdentity = await NostrIdentity.generate();
        await storage.saveIdentity({
          privateKeyHex: '', // We don't store private key in plain text
          publicKeyHex: userIdentity.publicKeyHex,
          npub: userIdentity.npub,
          createdAt: userIdentity.createdAt
        });
      }

      setIdentity(userIdentity);
      console.log('Socii identity initialized:', userIdentity.npub);

    } catch (err) {
      console.error('Failed to initialize Socii:', err);
      setError(err instanceof Error ? err.message : 'Failed to initialize');
    } finally {
      setIsLoading(false);
    }
  }, [storage]);

  const connect = async () => {
    if (!relayManager) return;

    try {
      setError(null);
      await relayManager.connect();
      setIsConnected(true);

      // Subscribe to current channel
      await switchChannel(currentChannel);

      console.log('Connected to Nostr relays');
    } catch (err) {
      console.error('Failed to connect:', err);
      setError(err instanceof Error ? err.message : 'Failed to connect');
    }
  };

  const sendMessage = async (content: string) => {
    if (!identity || !relayManager) {
      throw new Error('Not connected or identity not available');
    }

    try {
      // Create and sign the event
      const event = {
        pubkey: identity.publicKeyHex,
        created_at: Math.floor(Date.now() / 1000),
        kind: currentChannel === 'global' ? 1 : 20000, // Text note or geohash event
        tags: currentChannel === 'global' ? [] : [['g', currentChannel]],
        content
      };

      const signedEvent = await identity.signEvent(event);

      // Send to relays
      await relayManager.sendEvent(signedEvent);

      // Save to local storage
      const sociiMessage: SociiMessage = {
        ...signedEvent,
        channelId: currentChannel,
        geohash: currentChannel === 'global' ? undefined : currentChannel,
        messageType: currentChannel === 'global' ? 'public' : 'location'
      };

      await storage.saveMessage(sociiMessage);

      // Add to local state
      setMessages(prev => [...prev, sociiMessage]);

      console.log('Message sent successfully');
    } catch (err) {
      console.error('Failed to send message:', err);
      throw err;
    }
  };

  const switchChannel = async (channelId: string) => {
    if (!relayManager) return;

    try {
      setCurrentChannel(channelId);

      // Load messages from local storage
      const localMessages = await storage.getMessages(channelId);
      setMessages(localMessages);

      // Subscribe to new channel on relays
      if (isConnected) {
        const filter = {
          kinds: channelId === 'global' ? [1] : [20000],
          ...(channelId !== 'global' && { '#g': [channelId] }),
          since: Math.floor(Date.now() / 1000) - 3600 // Last hour
        };

        relayManager.subscribe(filter, async (event) => {
          const sociiMessage: SociiMessage = {
            ...event,
            channelId,
            geohash: channelId === 'global' ? undefined : channelId,
            messageType: channelId === 'global' ? 'public' : 'location'
          };

          // Save to local storage
          await storage.saveMessage(sociiMessage);

          // Add to state if not already present
          setMessages(prev => {
            const exists = prev.some(msg => msg.id === event.id);
            return exists ? prev : [...prev, sociiMessage];
          });
        });
      }

      console.log(`Switched to channel: ${channelId}`);
    } catch (err) {
      console.error('Failed to switch channel:', err);
      setError(err instanceof Error ? err.message : 'Failed to switch channel');
    }
  };

  const disconnect = () => {
    if (relayManager) {
      relayManager.disconnect();
      setIsConnected(false);
    }
  };

  useEffect(() => {
    initializeSocii();
  }, [initializeSocii]);

  const value: NostrContextType = {
    relayManager,
    identity,
    messages,
    currentChannel,
    isConnected,
    isLoading,
    error,
    connect,
    sendMessage,
    switchChannel,
    disconnect
  };

  return (
    <NostrContext.Provider value={value}>
      {children}
    </NostrContext.Provider>
  );
};
